import React, { useState, useRef, useEffect, useCallback } from 'react'
import { MessageCircle, X, Send, Loader2, Bot, User, ExternalLink, ChevronDown, Zap } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { cn } from '@/utils/cn'
import { ACCESS_TOKEN_KEY } from '@/api/client'

interface ChatMessage {
  role: 'user' | 'assistant'
  content: string
  action?: { type: string; url?: string; label?: string }
  entities?: { label: string; url: string }[]
  streaming?: boolean
  cached?: boolean
}

const WELCOME: ChatMessage = {
  role: 'assistant',
  content: "Hi! I'm your AR SalesMine Assistant. Ask me anything about your leads, deals, contacts, tasks, or pipeline — I'll look it up for you.",
}

export function ChatWidget() {
  const navigate = useNavigate()
  const [open, setOpen]       = useState(false)
  const [messages, setMessages] = useState<ChatMessage[]>([WELCOME])
  const [input, setInput]     = useState('')
  const [loading, setLoading] = useState(false)
  const bottomRef  = useRef<HTMLDivElement>(null)
  const inputRef   = useRef<HTMLTextAreaElement>(null)
  const abortRef   = useRef<AbortController | null>(null)

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 120)
  }, [open])

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const send = useCallback(async () => {
    const text = input.trim()
    if (!text || loading) return
    setInput('')

    // Append user bubble
    setMessages(prev => [...prev, { role: 'user', content: text }])
    setLoading(true)

    // Append empty assistant bubble (will be filled by stream)
    setMessages(prev => [...prev, { role: 'assistant', content: '', streaming: true }])

    const history = messages
      .filter(m => m.role === 'user' || m.role === 'assistant')
      .slice(-10)
      .map(m => ({ role: m.role, content: m.content }))

    const controller = new AbortController()
    abortRef.current = controller

    try {
      const token = localStorage.getItem(ACCESS_TOKEN_KEY)
      const resp = await fetch('/api/v1/chat/stream', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ message: text, history }),
        signal: controller.signal,
      })

      if (!resp.ok || !resp.body) {
        throw new Error(`HTTP ${resp.status}`)
      }

      const reader  = resp.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() ?? ''   // keep incomplete last line

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue
          const raw = line.slice(6).trim()
          if (!raw) continue

          let event: any
          try { event = JSON.parse(raw) } catch { continue }

          if (event.type === 'token') {
            setMessages(prev => {
              const msgs = [...prev]
              const last = msgs[msgs.length - 1]
              if (last?.role === 'assistant') {
                msgs[msgs.length - 1] = { ...last, content: last.content + event.content }
              }
              return msgs
            })
          } else if (event.type === 'done') {
            setMessages(prev => {
              const msgs = [...prev]
              const last = msgs[msgs.length - 1]
              if (last?.role === 'assistant') {
                msgs[msgs.length - 1] = {
                  ...last,
                  streaming: false,
                  cached: event.cached ?? false,
                  action: event.action ?? undefined,
                  entities: Array.isArray(event.entities) ? event.entities : undefined,
                }
              }
              return msgs
            })
          }
        }
      }
    } catch (err: any) {
      if (err?.name === 'AbortError') return
      const detail = err?.message?.includes('HTTP 4') || err?.message?.includes('HTTP 5')
        ? 'Server error. Please try again.'
        : 'Something went wrong. Please try again.'
      setMessages(prev => {
        const msgs = [...prev]
        const last = msgs[msgs.length - 1]
        if (last?.role === 'assistant' && last.streaming) {
          msgs[msgs.length - 1] = { role: 'assistant', content: `⚠️ ${detail}` }
        } else {
          msgs.push({ role: 'assistant', content: `⚠️ ${detail}` })
        }
        return msgs
      })
    } finally {
      setLoading(false)
      abortRef.current = null
    }
  }, [input, loading, messages])

  function handleKey(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send() }
  }

  function clearChat() {
    abortRef.current?.abort()
    setMessages([WELCOME])
    setLoading(false)
  }

  return (
    <>
      {/* Floating trigger */}
      <button
        onClick={() => setOpen(o => !o)}
        className={cn(
          // On mobile sit ABOVE the bottom nav (bottom-24) so the launcher never
          // overlaps the Settings tab; desktop keeps the corner position.
          'fixed bottom-24 right-4 lg:bottom-5 lg:right-5 z-50 w-12 h-12 rounded-full shadow-lg flex items-center justify-center transition-all duration-200',
          'bg-[var(--accent)] text-white hover:scale-110 active:scale-95',
        )}
        aria-label="Open AI Assistant"
      >
        {open ? <X size={20} /> : <MessageCircle size={20} />}
      </button>

      {/* Chat panel */}
      {open && (
        <div
          className={cn(
            'fixed bottom-20 right-5 z-50 w-80 sm:w-96 rounded-2xl overflow-hidden flex flex-col',
            'border shadow-2xl dark:border-slate-700 border-gray-200',
            'dark:bg-slate-900 bg-white',
          )}
          style={{ height: '520px' }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 flex-shrink-0" style={{ background: 'var(--accent)' }}>
            <div className="flex items-center gap-2">
              <Bot size={16} className="text-white" />
              <span className="text-sm font-semibold text-white">CRM Assistant</span>
              <span className="text-[10px] text-white/90 bg-white/20 px-1.5 py-0.5 rounded-full flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-green-400 inline-block" /> Online
              </span>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={clearChat} className="text-white/70 hover:text-white text-[10px] px-2 py-0.5 rounded hover:bg-white/20 transition-colors">
                Clear
              </button>
              <button onClick={() => setOpen(false)} className="text-white/70 hover:text-white p-1 rounded hover:bg-white/20 transition-colors">
                <ChevronDown size={16} />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
            {messages.map((msg, i) => (
              <div key={i} className={cn('flex gap-2', msg.role === 'user' ? 'flex-row-reverse' : 'flex-row')}>
                {/* Avatar */}
                <div className={cn(
                  'w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5',
                  msg.role === 'user' ? 'bg-[var(--accent-50)] dark:bg-slate-700' : 'bg-[var(--accent)] text-white',
                )}>
                  {msg.role === 'user' ? <User size={12} className="text-[var(--accent)]" /> : <Bot size={12} />}
                </div>

                {/* Bubble */}
                <div className={cn('max-w-[82%] flex flex-col gap-1.5', msg.role === 'user' ? 'items-end' : 'items-start')}>
                  <div className={cn(
                    'px-3 py-2 rounded-2xl text-xs leading-relaxed whitespace-pre-wrap',
                    msg.role === 'user'
                      ? 'bg-[var(--accent)] text-white rounded-tr-sm'
                      : 'dark:bg-slate-800 bg-gray-100 dark:text-slate-200 text-gray-800 rounded-tl-sm',
                  )}>
                    {msg.content || (msg.streaming ? '' : '…')}
                    {/* Blinking cursor while streaming */}
                    {msg.streaming && (
                      <span className="inline-block w-[2px] h-[12px] bg-current ml-0.5 align-middle animate-pulse" />
                    )}
                  </div>

                  {/* Cache hit badge */}
                  {msg.cached && (
                    <div className="flex items-center gap-1 text-[9px] text-emerald-500">
                      <Zap size={9} />
                      <span>Instant (cached)</span>
                    </div>
                  )}

                  {/* Per-record deep-links — open the specific lead/deal/contact/task */}
                  {!msg.streaming && msg.entities && msg.entities.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {msg.entities.map((e, ei) => (
                        <button
                          key={ei}
                          onClick={() => { navigate(e.url); setOpen(false) }}
                          className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] bg-[var(--accent-50)] dark:bg-slate-700 text-[var(--accent)] dark:text-slate-200 hover:underline max-w-[180px]"
                          title={e.label}
                        >
                          <ExternalLink size={9} className="flex-shrink-0" />
                          <span className="truncate">{e.label}</span>
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Navigate action (list / filtered view) */}
                  {!msg.streaming && msg.action?.url && (
                    <button
                      onClick={() => { navigate(msg.action!.url!); setOpen(false) }}
                      className="flex items-center gap-1 text-[10px] text-[var(--accent)] hover:underline"
                    >
                      <ExternalLink size={10} />
                      {msg.action.label || 'View'}
                    </button>
                  )}
                </div>
              </div>
            ))}

            {/* Thinking indicator — shown only before first token arrives */}
            {loading && messages[messages.length - 1]?.content === '' && messages[messages.length - 1]?.streaming && (
              <div className="flex gap-2 items-start -mt-2">
                <div className="invisible w-6" />
                <div className="px-3 py-2 rounded-2xl rounded-tl-sm dark:bg-slate-800 bg-gray-100 flex items-center gap-1.5">
                  <Loader2 size={12} className="animate-spin text-[var(--accent)]" />
                  <span className="text-xs dark:text-slate-400 text-gray-500">Thinking…</span>
                </div>
              </div>
            )}

            <div ref={bottomRef} />
          </div>

          {/* Input */}
          <div className="px-3 pb-3 flex-shrink-0 border-t dark:border-slate-800 border-gray-100 pt-2">
            <div className="flex items-end gap-2 dark:bg-slate-800 bg-gray-50 rounded-xl border dark:border-slate-700 border-gray-200 px-3 py-2">
              <textarea
                ref={inputRef}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKey}
                placeholder="Ask about leads, deals, tasks…"
                rows={1}
                disabled={loading}
                className="flex-1 bg-transparent text-xs dark:text-slate-200 text-gray-800 placeholder-gray-400 dark:placeholder-slate-500 outline-none resize-none max-h-20 leading-relaxed disabled:opacity-50"
                style={{ scrollbarWidth: 'none' }}
              />
              <button
                onClick={send}
                disabled={!input.trim() || loading}
                className="w-7 h-7 rounded-lg flex items-center justify-center transition-all disabled:opacity-30 flex-shrink-0"
                style={{ background: 'var(--accent)' }}
              >
                <Send size={13} className="text-white" />
              </button>
            </div>
            <p className="text-[9px] text-center dark:text-slate-600 text-gray-400 mt-1.5">
              Powered by AR Tech Solutions
            </p>
          </div>
        </div>
      )}
    </>
  )
}
